import { Component, EventEmitter, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { DetailPageService } from '@services/detail-page.service';
import { LoaderService } from '@services/support/loader.service';
import { SnackbarService } from '@services/support/snackbar.service';
import { UserService } from '@services/user.service';
import { UtilityService } from '@services/remote/utility.service';
import { WorkflowService } from '@services/workflow.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { debounceTime, Subscription } from 'rxjs';
import { AlertModalComponent } from 'src/app/components/modals/alert-modal/alert-modal.component';
import { User } from '../../../../models/User'
import { UserDetailComponent } from '../user-detail/user-detail.component';

@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserListComponent implements OnInit, OnDestroy {

  @ViewChild('optionsDropdown') optionsDropdown: any;
  @ViewChild('menu') menu: any;
  @ViewChild(MatSort, { static: false })
  set sort(sorter: MatSort) {
    this.dataSource.sort = sorter;
  }

  dataSource = new MatTableDataSource<any>()
  displayedColumns: string[] = [];
  displayedTeamColumns: string[] = [
    "teamname",
    "members",
    "actions"
  ];
  filter = new UntypedFormControl()

  currentUser: User = null;

  userList: User[] = []
  workflowUserLimit : number
  expertLimit: number
  expertCount: number = 0

  subscriptions: Subscription[] = []

  dataLoaded = false;
  showCountPanel = false

  workflowUserCount = 0
  newDeactivatedWorkflows = 0;
  newActivatedWorkflows = 0;
  activatedUsers = []
  deactivatedUsers = []
  total

  formGroup = new UntypedFormGroup({});
  tabIndex = 0

  userDetailOpen: boolean = false;

  onRoomCreate = new EventEmitter<void>();

  noWorkflowLicense = true
  accountRemoteEnabled = false

  constructor( 
    private workflowService: WorkflowService,
    private userService: UserService,
    private authService: AuthService,
    private loaderService: LoaderService,
    private snackBarService: SnackbarService,
    private modalService: BsModalService,
    private detailPageService: DetailPageService,
    private utilityService: UtilityService,
    private translateService: TranslateService) {}

  ngOnInit(): void {
    this.setDisplayedColumns();
    this.getAllUsers()

    this.formGroup.valueChanges.pipe().subscribe(res => {
      this.showCountPanel = true
    })
    if(this.filter.value){
      this.filterUsers(this.filter.value.toLowerCase());
    }else{
      this.dataSource = new MatTableDataSource(this.userList)
    }
    this.filter.valueChanges.pipe(debounceTime(300)).subscribe(res => {
      if (res) {
        this.filterUsers(res.toLowerCase());
      } else {
        this.dataSource = new MatTableDataSource(this.userList)
        this.setupDataSourceSort(this.dataSource);
      }
    })
    this.currentUser = this.authService.currentUser;

    Promise.all([
      this.authService.getAccountData(),
      this.utilityService.getServerTimeOffset()
    ]).then(([account, offset]) => {
      const currentTime = (new Date()).getTime()

      const license = account.data.accountData.license
      const now = currentTime + offset
      const twelveHoursInMs = 1000*60*60*12

      this.expertLimit = license.limit2 || 0
      this.noWorkflowLicense = !license.workflow_account || (license.workflow_start - twelveHoursInMs > now) || (license.workflow_end + twelveHoursInMs < now)
      this.accountRemoteEnabled = !!license.remote_account
      if (!this.accountRemoteEnabled && this.tabIndex === 2) {
        this.tabIndex = 0;
      }
      this.setDisplayedColumns();
    });
  }

  setDisplayedColumns(): void {
    if (this.noWorkflowLicense) {
      this.displayedColumns = [
        "name",
        "username",
        "actions"
      ];
    } else {
      this.displayedColumns = [
        "workflowUser",
        "name",
        "username",
        "actions"
      ];
    }
  }

  getAllUsers(){
    this.loaderService.show()
    this.workflowService.getAllUsersForWorkflow().then(res => {
      this.workflowUserCount = res.workflowUserCount
      this.workflowUserLimit = res.workflowUserLimit

      this.userList = res.users.filter(user => user.auth && !user.auth.deleted)
        .sort((a, b) => {
          const usernameA = (a.ad_user ? a.user_principal_name : a.auth?.username)?.toLowerCase() || '';
          const usernameB = (b.ad_user ? b.user_principal_name : b.auth?.username)?.toLowerCase() || '';
          return usernameA.localeCompare(usernameB);
        });
      
      // Expert sayısını hesapla
      this.expertCount = this.userList.filter(user => user.role === 'expert').length;
      this.dataSource = new MatTableDataSource(this.userList)
      this.setupDataSourceSort(this.dataSource);

      this.userList.forEach(user => {
        if(user.workflow_user) {
          this.activatedUsers.push(user.id)
        }else{
          this.deactivatedUsers.push(user.id)
        }
        const hasWorkflow = user.workflow_user
        const userFormControl = new UntypedFormControl (hasWorkflow)

        userFormControl.valueChanges.subscribe((newValue) => {
          const userHasWorkflow = this.userList.find((item) => item.id === user.id && item.workflow_user)
          if(!userHasWorkflow && newValue) {
            this.newActivatedWorkflows += 1
          }
          else if(!userHasWorkflow && !newValue){
            this.newActivatedWorkflows -= 1
          }
          else if(userHasWorkflow && !newValue) {
            this.newDeactivatedWorkflows +=1
          }else if(userHasWorkflow && newValue){
            this.newDeactivatedWorkflows -=1
          }
          this.total = this.newActivatedWorkflows > this.newDeactivatedWorkflows ? this.newActivatedWorkflows - this.newDeactivatedWorkflows : 0
        })
        this.formGroup.addControl(user.id, userFormControl)
      });

      this.dataLoaded = true
      this.loaderService.hide()
    })
  }

  saveWorkflowUsers(): void {
    this.loaderService.show()
    const insertedUsers = this.userList.filter((user) => !this.hasWorkflow(user) && this.formGroup.value[user.id]).map((user) => user.id)
    const disabledWorkflowUsers = this.userList.filter((user) => this.hasWorkflow(user) && !this.formGroup.value[user.id]).map((user) => user.id)
    this.workflowService.saveDisabledUsers(insertedUsers, disabledWorkflowUsers).then(res => {
      this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.USER-LIST.SAVE-SUCCESSFUL'))
      this.showCountPanel = false
      this.newDeactivatedWorkflows = 0
      this.newActivatedWorkflows = 0

      this.userList.forEach((user) => {
        if (disabledWorkflowUsers.includes(user.id)) {
          delete user.workflows
          delete user.workflow_user
        }
        if (insertedUsers.includes(user.id)) {
          user.workflow_user = true;
        }
      })
      this.showCountPanel = false
      this.newDeactivatedWorkflows = 0
      this.workflowUserCount = this.workflowUserCount - disabledWorkflowUsers.length + insertedUsers.length
    })
    .catch(err => {
      if(err.error == 'no-workflow-user-limit') {
        this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.USER-LIST.LIMIT-EXCEED'))
      }else{
        this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.USER-LIST.ERROR'))
      }
    })
    .finally(() => this.loaderService.hide())
  }

  cancel(): void {
    const disabledWorkflowUsers = this.userList.filter((user) => this.hasWorkflow(user) && !this.formGroup.value[user.id]).map((user) => user.id)
    const enabledWorkflowUsers = this.userList.filter((user) => !this.hasWorkflow(user) && this.formGroup.value[user.id]).map((user) => user.id)

    disabledWorkflowUsers.forEach(id => {
      this.formGroup.controls[id].patchValue(true) 
    });
    enabledWorkflowUsers.forEach(id => {
      this.formGroup.controls[id].patchValue(false) 
    });
    
    this.showCountPanel = false
    this.newDeactivatedWorkflows = 0
    this.newActivatedWorkflows = 0
  }

  private hasWorkflow(user) {
    return user.workflow_user;
  }

  private setupDataSourceSort(dataSource: MatTableDataSource<any>): void {
    dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case "workflowUser":
          return this.formGroup.controls[item.id]?.value
        case "username":
          return item.ad_user ? item.user_principal_name?.toLowerCase() : item.auth?.username?.toLowerCase()
        default:
          return item[property]
      }
    };
    dataSource.sort = this.sort;
  }

  private filterUsers(filterData?) {
    let filteredData =[]
    filteredData = this.userList.filter(user => {
      if(user){
        return user.name.toLowerCase().includes(filterData.toLowerCase())
      }
      else{
       return filteredData
      }
       })
    this.dataSource = new MatTableDataSource(filteredData);
    this.setupDataSourceSort(this.dataSource);
  }

  onDelete(userId: string) {
    const initialState = {
      textMessage : this.translateService.instant('MAIN.CONTENT.USER-LIST.DELETE-OR-NOT'),
      confirmButtonText : this.translateService.instant('MAIN.CONTENT.USER-LIST.ACTIONS.DELETE'),
      headerTitle: this.translateService.instant('MAIN.CONTENT.WORKFLOW.HEADER-TITLES.DELETE-USER')
    };
    let alertmodalRef = this.modalService.show(AlertModalComponent, {
      initialState,
      backdrop: 'static',
      class: 'modal-dialog-centered',
      animated: false
    });
    alertmodalRef.content.onClose.subscribe(res => {
      if (res) {
        this.loaderService.show();
        this.userService.deleteUser(userId)
        .then(() => {
          this.snackBarService.success(this.translateService.instant('MAIN.USERS.USER_DETAILS.DELETE_USER_MODAL.DELETED'));
        })
        .catch(() => {
          this.snackBarService.error(this.translateService.instant('MAIN.USERS.USER_DETAILS.DELETE_USER_MODAL.ERROR'));
        })
        .finally(() => {
          this.getAllUsers();
          this.loaderService.hide();
        });
      } else {
        alertmodalRef.hide();
      }
    })
  }

  canResendInvite(user: User): boolean {
    return user?.inviteStatus === 'pending' && !user?.ad_user && !user?.oauth_user_id;
  }

  canSendResetPassword(user: User): boolean {
    return user?.inviteStatus !== 'pending' && !user?.ad_user && !user?.oauth_user_id;
  }

  onResendInvite(user: User) {
    this.loaderService.show();
    this.userService.sendUserInvite(user.id)
    .then(() => {
      this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.USER-DETAIL.INVITE-RESENT'));
    })
    .catch(() => {
      this.snackBarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'));
    })
    .finally(() => this.loaderService.hide());
  }

  onSendResetPassword(user: User) {
    this.loaderService.show();
    this.userService.sendUserPasswordReset(user.id)
    .then(() => {
      this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.USER-DETAIL.RESET-SENT'));
    })
    .catch(() => {
      this.snackBarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'));
    })
    .finally(() => this.loaderService.hide());
  }

  onEdit(user): void {
    this.userDetailOpen = true;
    const [instance, onClose] = this.detailPageService.loadComponent(UserDetailComponent)
    instance.user = user;
    instance.detailMode = 'edit';
    onClose.then(() => {
      this.userDetailOpen = false;
      this.getAllUsers();
    })
  }

  onCreate(): void {
    this.userDetailOpen = true;
    const [instance, onClose] = this.detailPageService.loadComponent(UserDetailComponent)
    instance.detailMode = 'create';
    instance.workflowUserLimit = this.workflowUserLimit;
    instance.workflowUserCount = this.workflowUserCount;
    onClose.then(() => {
      this.userDetailOpen = false;
      this.getAllUsers();
    })
  }

  openUserDetail(user): void {
    this.userDetailOpen = true;
    const [instance, onClose] = this.detailPageService.loadComponent(UserDetailComponent)
    instance.user = user;
    onClose.then(() => {
      this.userDetailOpen = false;
      this.getAllUsers();
    })
  }

  ngOnDestroy(): void {
    this.detailPageService.removeComponent();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  onTabClicked(index: number): void {
    if (index === 2 && !this.accountRemoteEnabled) {
      this.tabIndex = 0;
      return;
    }

    this.tabIndex = index;
    this.dataLoaded = false;

    if (index === 0) {
      this.setDisplayedColumns();
    } else if (index === 1) {
      this.displayedColumns = [
        "teamname",
        "members",
        "actions"
      ];
      this.getAllTeams(); 
    }

    this.dataLoaded = true;
  }

  getAllTeams() {
    // henüz yok
  }

  onCreateRoom() {
    this.onRoomCreate.emit()
  }

  getRoleBadgeClass(user: User): string {
    const role = user.coadmin ? 'coadmin' : user.role;
    switch(role) {
      case 'admin':
        return 'badge-admin';
      case 'coadmin':
        return 'badge-coadmin';
      case 'expert':
        return 'badge-expert';
      case 'user':
        return 'badge-user';
      default:
        return 'badge-user';
    }
  }

  getRoleLabel(user: User): string {
    const role = user.coadmin ? 'coadmin' : user.role;
    switch(role) {
      case 'admin':
        return 'Admin';
      case 'coadmin':
        return 'Co-Admin';
      case 'expert':
        return 'Expert';
      case 'user':
        return 'User';
      default:
        return 'User';
    }
  }
}
